import React, { ReactNode, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { isEqualWith, isNull } from "lodash";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import moment from "moment";

import { formatDate } from "helpers/Helpers";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select, Select as SelectNew } from "components/select/";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import "pages/secure/facility/product/Product.scss";
import ReactDOM from "react-dom";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import CustomerImage from "elements/customer/CustomerImage";

import Sheet from "components/sheet/Sheet";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import ProductSelect from "components/productModal/ProductSelect";

import {
  GetMembership,
  UploadMembershipImage,
  RemoveMembershipImage,
  PutMembership,
  PutMembershipArchive,
} from "api/rpc/2024-04/facilityAdmin/membership/membership";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";

import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";

import { ArchiveVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { GetProduct, PutArchiveProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct } from "redux/reducers/models/product";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TFunction } from "i18next";

export const MEMBERSHIP_EXPIRY_OPTIONS = (t: TFunction) => [
  {
    label: t("No Expiry"),
    value: "no_expiry",
  },
  {
    label: t("One Year"),
    value: "one_year",
  },
  {
    label: t("End of Year"),
    value: "end_of_year",
  },
  {
    label: t("Custom Date"),
    value: "custom_date",
  },
];

export interface IMembershipState {
  id: number;
  client_id: number;
  customer_type_id: number | null;
  customer_type_title: string | null;


  product_id: number | null;
  variant_id: number | null;

  product_title?: string | null;

  title: string;
  subtitle: string | null;
  expiry: string | null;
  expiry_type: string;
  type: string;
  membership_type: string;
  membership_logo_image: string;
  membership_card_image: string;
}

interface IImageState {
  membership_logo_image: string;
  membership_card_image: string;
}

export default function Single() {
  const { Option } = SelectNew;
  const { t } = useTranslation();
  const { membershipId } = useParams<{ membershipId: string }>();
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [renderFlag, setRenderFlag] = useState({
    membershipLoaded: false,
    inventoryLoaded: false,
    hasExpiry: false,
    prevMembershipState: undefined as Partial<IMembershipState>,
  });

  const [membershipState, setMembershipState] = useState<Partial<IMembershipState>>({
    id: null,
    title: "",
    subtitle: "",
    type: "",
    customer_type_id: null,
    expiry: "",
    expiry_type: "",
    variant_id: null,
    product_id: null,
    product_title: null,
  });

  const [optionState, setOptionState] = useState({
    customerTypes: [],
  });

  const [filterState, setFilterState] = useState({
    customerTypeSearch: "",
  });

  const [imageState, setImageState] = useState<IImageState>({
    membership_logo_image: "",
    membership_card_image: "",
  });

  const [dateState, setDateState] = useState({
    selectedDate: new Date(),
  });

  const {
    state: archiveModal,
    updateModal: updateArchiveModal,
    closeModal: closeArchiveModal,
  } = useModal();

  const [productState, setProductState] = useState<IProduct>(null);


  const [productModalState, setProductModalState] = useState({
    isOpen: false,
    selectedProducts: [] as any[],
  });

  useEffect(() => {
    void loadMembership();
  }, [membershipId]);

  useEffect(() => {
    void loadCustomerTypes();
  }, [membershipState.membership_type]);

  async function loadCustomerTypes() {
    const customerTypeRes = await GetCustomerType(
      { application: membershipState.membership_type === "membership" ? "green_fee" : "power_cart" },
      true,
    );
    if (customerTypeRes.status !== StatusCode.OK) {
      return;
    };

    const defaultCustomerType = customerTypeRes?.data?.find(
      customerType => customerType.id === membershipState.customer_type_id,
    );

    ReactDOM.unstable_batchedUpdates(() => {
      setOptionState(prev => ({
        ...prev,
        customerTypes: customerTypeRes.data,
      }));
      setFilterState({ customerTypeSearch: defaultCustomerType?.full_title ?? "" });
    });
  }

  async function loadMembership() {
    const membershipRes = await GetMembership({ id: Number(membershipId) }, true);
    if (membershipRes.status !== StatusCode.OK) {
      return;
    }

    const membership = membershipRes.data[0];


    if (membership.product_id) {
      const productRes = await GetProduct({ id: Number(membership.product_id), variants: true }, true);
      if (productRes.status === StatusCode.OK) {
        setProductState(productRes.data?.[0] ?? null);
      } else {
        setProductState(null);
      }
    } else {
      setProductState(null);
    }

    if (membership.expiry !== null) {
      const notOffsetDate = moment(membership.expiry).toDate();
      setDateState(prev => ({
        ...prev,
        selectedDate: notOffsetDate,
      }));
    } else {
      setDateState({
        ...dateState,
        selectedDate: new Date(),
      });
    }

    setMembershipState({
      id: membership.id,
      title: membership.title,
      subtitle: membership.subtitle,
      type: membership.type,
      customer_type_id: membership.customer_type_id,
      customer_type_title: membership.customer_type_title,
      expiry: membership.expiry,
      expiry_type: membership?.expiry_type,
      membership_type: membership.membership_type,
      variant_id: membership.variant_id ?? null,
      product_id: membership.product_id ?? null,

    });

    setImageState(prev => ({
      ...prev,
      membership_logo_image: membership.logo_image_source,
      membership_card_image: membership.card_image_source,
    }));

    setRenderFlag({
      ...renderFlag,
      membershipLoaded: true,
      hasExpiry: !!membership.expiry,
      prevMembershipState: {
        id: membership.id,
        title: membership.title,
        subtitle: membership.subtitle,
        type: membership.type,
        customer_type_id: membership.customer_type_id,
        customer_type_title: membership.customer_type_title,
        expiry: membership.expiry,
        expiry_type: membership?.expiry_type,
        membership_type: membership.membership_type,
        variant_id: membership.variant_id,
        product_id: membership.product_id,
      },
    });
  }

  async function saveMembership() {
    const membershipRes = await PutMembership({ ...membershipState, expiry: membershipState?.expiry_type === "custom_date" ? dateState?.selectedDate : undefined }, true);
    if (membershipRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.membership.011")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.product.membership.012")));
    ReactDOM.unstable_batchedUpdates(() => {
      //reset the editing state of the page
      setMembershipState(prev => ({ ...prev, expiry: membershipRes?.data?.expiry }));
      setRenderFlag({ ...renderFlag, prevMembershipState: { ...membershipState, expiry: membershipRes?.data?.expiry } });
      // setFilterState({ ...filterState, customerTypeSearch: membershipRes.data.customer_type_title ?? "" });
      const notOffsetDate = membershipRes?.data?.expiry ? moment(membershipRes?.data?.expiry as string).toDate() : new Date();
      setDateState({
        ...dateState,
        selectedDate: notOffsetDate,
      });
    });

    return;
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setMembershipState(prev => ({ ...prev, [id]: value }));
  }

  // function handleCheckboxChange(event: any) {
  //   const { id, checked } = event.target;

  //   // separate expiry logic to handle date
  //   if (id === "hasExpiry") {
  //     setRenderFlag({ ...renderFlag, hasExpiry: checked });

  //     if (checked && renderFlag.prevMembershipState.expiry === null) {
  //       setDateState({ ...dateState, selectedDate: new Date() });
  //       setMembershipState({ ...membershipState, expiry: formatDate(new Date()) });
  //       return;
  //     }

  //     if (checked && renderFlag.prevMembershipState.expiry !== null) {
  //       const notOffsetDate = new Date((renderFlag.prevMembershipState.expiry) + "EST");

  //       setDateState({ ...dateState, selectedDate: notOffsetDate });
  //       setMembershipState({ ...membershipState, expiry: renderFlag.prevMembershipState.expiry });
  //       return;
  //     }

  //     if (!checked) {
  //       setMembershipState({ ...membershipState, expiry: null });
  //       setDateState({ ...dateState, selectedDate: new Date() }); //"forgets" any date changes
  //       return;
  //     }
  //   }

  //   setMembershipState({ ...membershipState, [id]: checked });
  // }

  function handleDateSelector(selectedDate: Date) {
    setMembershipState(prev => ({ ...prev, expiry: formatDate(selectedDate) }));
    setDateState(prev => ({ ...prev, selectedDate }));
  }

  function unsavedChangesExist() {
    const { prevMembershipState, membershipLoaded } = renderFlag;
    if (prevMembershipState === undefined) {
      if (membershipLoaded) {
        setRenderFlag(prev => ({ ...prev, prevMembershipState: membershipState }));
      }
      return false;
    }

    return !isEqualWith(prevMembershipState, membershipState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    const defaultCustomerType = optionState?.customerTypes?.find(
      customerType => customerType.id === renderFlag?.prevMembershipState?.customer_type_id,
    );
    ReactDOM.unstable_batchedUpdates(() => {
      setMembershipState({ ...renderFlag.prevMembershipState });
      setFilterState(prev => ({ ...prev, customerTypeSearch: defaultCustomerType?.full_title ?? "" }));
    });


    //only necessary with expiry possibly NULL
    // const prevExpiryVal: string = renderFlag.prevMembershipState.expiry;
    // if (prevExpiryVal === null) {
    //   setRenderFlag({ ...renderFlag, hasExpiry: false });
    // } else {
    //   //"reset" expiry on page
    //   setRenderFlag({ ...renderFlag, hasExpiry: true });

    //   const notOffsetDate = new Date(prevExpiryVal + "EST");
    //   setDateState({ ...dateState, selectedDate: notOffsetDate });
    //   setMembershipState({ ...membershipState, expiry: prevExpiryVal });
    // }
  }

  async function saveMembershipLogoImage(imageFile: File) {
    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("id", membershipState.id.toString());
    formData.append("type", "logo");

    const imageRes = await UploadMembershipImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    };

    setImageState(prev => ({ ...prev, membership_logo_image: imageRes.data.logo_image_source }));
  }

  async function saveMembershipCardImage(imageFile: File) {
    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("id", membershipState.id.toString());
    formData.append("type", "card");

    const imageRes = await UploadMembershipImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    setImageState(prev => ({ ...prev, membership_card_image: imageRes.data.card_image_source }));
  }

  async function deleteMembershipImage(imageType: "logo" | "card") {
    const deleteRes = await RemoveMembershipImage(
      { id: membershipState.id, type: imageType },
      true,
    );

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.membership.013")));
    }

    dispatch(showSuccess(t("secure.facility.product.membership.014")));
    setImageState(prev => ({
      ...prev,
      membership_card_image: deleteRes.data.card_image_source,
      membership_logo_image: deleteRes.data.logo_image_source,
    }));
  }

  async function archiveMembership() {
    try {
      let response;

      if (!productState) {
        response = await PutMembershipArchive({ id: membershipState.id }, true);
      } else if (productState.variant_count === 1) {
        response = await PutArchiveProduct({ id: membershipState.product_id }, true);
      } else {
        response = await ArchiveVariant({ id: membershipState.variant_id }, true);
      }

      if (response.status === StatusCode.OK) {
        dispatch(showSuccess(t("Membership archived successfully")));
        closeArchiveModal();
        history.push("/admin/membership");
      } else {
        dispatch(showError(t("Failed to archive membership")));
      }
    } catch (error) {
      dispatch(showError(t("Error archiving membership")));
      console.error("Archive error:", error);
    }
  }

  console.log(productModalState);


  function handleOpenProductModal() {
    setProductModalState({ isOpen: true, selectedProducts: [] });
  }

  function handleCloseProductModal() {
    setProductModalState({ isOpen: false, selectedProducts: [] });
  }

  function handleProductSelection(products: any[]) {
    console.log(products);
    setProductModalState(prev => ({ ...prev, selectedProducts: products[products?.length - 1] ? [products[products?.length - 1]] : [] }));
  }

  function handleSaveProduct() {
    if (productModalState.selectedProducts.length > 0) {
      const selectedProduct = productModalState.selectedProducts[0];
      const selectedVariant = selectedProduct.variants?.[0];

      setMembershipState(prev => ({
        ...prev,
        product_id: selectedProduct.id,
        variant_id: selectedVariant?.id ?? prev.variant_id,
        product_title: selectedProduct.preferred_title || selectedProduct.title,
      }));


      setProductState(selectedProduct as IProduct);
    }

    handleCloseProductModal();
  }


  function getProductBadgeLabel(product: IProduct | null) {
    if (!product) {
      return;
    };

    const baseTitle = product.preferred_title ? product.preferred_title : product.title;

    if (product.variant_count > 1 && Array.isArray(product.variants)) {
      const variant = product.variants
        .find(v => v.id === membershipState.variant_id);



      if (variant) {
        return `${baseTitle} - ${variant.title}`;
      }
    }

    return baseTitle;
  }



  // function handleRemoveProduct() {
  //   setMembershipState(prev => ({
  //     ...prev,
  //     product_id: null,
  //     variant_id: null,
  //     product_title: null,
  //   }));
  //   setProductState(null);
  // }

  function getExpiryDate() {
    switch (membershipState?.expiry_type) {
      case "end_of_year":
        return moment()?.endOf("year")?.format('ll');
      case "one_year":
        return moment()?.add(1, "year")?.format("ll");
      default:
        return moment(membershipState?.expiry, "YYYY-MM-DD")?.format("ll");
    }
  }

  function renderConfiguration(): ReactNode[] {
    return [
      <Select
        key={1}
        label={t("secure.facility.product.membership.007")}
        placeholder={t("secure.facility.product.membership.008")}
        showSearch
        className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
        onSearch={(query: string) => {
          setFilterState({ ...filterState, customerTypeSearch: query });
          if (query === "") {
            setMembershipState(prev => ({ ...prev, customer_type_id: null }));
          }
        }}
        onChange={(value: number) => setMembershipState({ ...membershipState, customer_type_id: value })}
        allowClear
        defaultValue={membershipState?.customer_type_id}
        searchValue={filterState.customerTypeSearch}
        showDropDownOnFocus={true}
        hideDropdown={true}
        handleClear={() => setMembershipState(prev => ({ ...prev, customer_type_id: null }))}
      >
        {optionState.customerTypes
          .filter((custType: any) =>
            String(custType.full_title).toLowerCase().includes(filterState.customerTypeSearch.toLowerCase()),
          )
          .map((type: any, i: number) => (
            <div key={i}>
              <div>
                <Option key={i} value={type.id} name={type.full_title}>
                  {type?.full_title}
                </Option>
              </div>
            </div>
          ))}
      </Select>,
      <Select
        key={2}
        label={t("Membership Expiry")}
        placeholder={t("Select membership expiry")}
        onChange={value => setMembershipState({ ...membershipState, expiry_type: value })}
        defaultValue={membershipState?.expiry_type ?? ""}
      >
        {MEMBERSHIP_EXPIRY_OPTIONS(t)?.map((option, i: number) => {
          return (
            <Option key={i} value={option?.value}>
              {option?.label}
            </Option>
          );
        })}
      </Select>,
      <React.Fragment key={3}>
        {membershipState?.expiry_type === "custom_date" ? (
          <div id="membership-date-selector">
            <DatePickerInput
              months={1}
              label={t("Expires On")}
              position="center"
              startingDate={dateState.selectedDate}
              setStartingDate={handleDateSelector}
            />
          </div>
        ) : membershipState?.expiry_type !== "no_expiry" ? (
          <Input
            label={t("Expires on")}
            readOnly
            value={membershipState?.expiry_type === renderFlag?.prevMembershipState?.expiry_type ? moment(renderFlag?.prevMembershipState?.expiry, "YYYY-MM-DD")?.format("ll")?.toString() : getExpiryDate()} />
        ) : null}
      </React.Fragment>,
    ];
  }

  return (
    <>
      <Page
        title={membershipState.title}
        breadcrumbs={[
          {
            label: t("secure.facility.product.membership.017"),
            prefix: true,
            url: "/admin/membership",
          },
        ]}
        narrow
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveMembership,
          onCancel: cancelUnsavedChanges,
        }}
        multipleActionDropdownAction={{
          label: t("More Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Archive"),
                handler: () => updateArchiveModal({ isOpen: true }),
                disabled: false,
                icon: "book-open",
              },
            ],
          },
        }}
      >
        <Card title={t("secure.facility.product.membership.002")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={membershipState.title}
                  label={t("secure.facility.product.membership.003")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("secure.facility.product.membership.004")}
                />
                <Input
                  value={membershipState.subtitle}
                  label={t("secure.facility.product.membership.005")}
                  id="subtitle"
                  onChange={handleInputChange}
                  placeholder={t("secure.facility.product.membership.006")}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                {windowSize.width <= TABLET_WIDTH ? (
                  <div className="flex flex-column gap-3">{renderConfiguration()}</div>
                ) : (
                  renderConfiguration()
                )}
              </FormLayout.Group>

            </FormLayout>
          </Card.Section>
          <Card.Section>

            {/* NEW: Product attach UI */}
            {!productState && (
              <div>
                <Button type="secondary" size="medium" onClick={handleOpenProductModal}>
                  {t("Attach Product")}
                </Button>
              </div>
            )}

            {productState && (
              <div>
                <div>
                  <span className="block font-semibold text-gray-700 mb-2">
                    Attached Products
                  </span>
                  <Badge
                    type="primary"
                    size="large"
                    onClick={() => history.push(`/admin/product/${productState.id}`)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    {getProductBadgeLabel(productState)} <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-2" />
                  </Badge>
                </div>

                {/* optional remove -  */}
                {/*
                  <Button type="link-color" size="small" onClick={handleRemoveProduct}>
                    {t("Remove")}
                  </Button>
                  */}
              </div>
            )}
          </Card.Section>
        </Card>

        <Card>
          <Card.Section
            title={t("secure.facility.product.membership.018")}
            subtitle={t("secure.facility.product.membership.019")}
          >
            <div className="stats_bar-picture">
              <CustomerImage
                imageSource={imageState.membership_logo_image}
                saveProfileImage={imageFile => saveMembershipLogoImage(imageFile)}
                deleteProfileImage={() => deleteMembershipImage("logo")}
              />
            </div>
          </Card.Section>

          <Card.Section
            title={t("secure.facility.product.membership.020")}
            subtitle={t("secure.facility.product.membership.021")}
          >
            <div className="stats_bar-picture">
              <CustomerImage
                imageSource={imageState.membership_card_image}
                saveProfileImage={imageFile => saveMembershipCardImage(imageFile)}
                deleteProfileImage={() => deleteMembershipImage("card")}
              />
            </div>
          </Card.Section>
        </Card>

        <Portal isMounted={archiveModal.isOpen}>
          <Popup
            okText={t("Confirm")}
            onCancel={() => closeArchiveModal()}
            onOk={() => archiveMembership()}
            title={t("Confirm Archiving Membership")}
            type="warning"
            description={t("Archiving this membership will also archive the associated variant in your product list")}
            open={archiveModal.isOpen}
          />
        </Portal>
      </Page>

      {/* NEW: Product picker modal */}
      <Sheet
        open={productModalState.isOpen}
        onCancel={handleCloseProductModal}
        onOk={handleSaveProduct}
        title={t("Select Membership Product")}
        size="large"
        closable
        okText={t("Save")}
        cancelText={t("Cancel")}
        okDisabled={productModalState.selectedProducts.length === 0}
        height="flexible"
      >
        <ProductSelect
          selectedProducts={productModalState.selectedProducts}
          onChange={handleProductSelection}
          search={true}
          getProductsParams={{
            type: "membership",
          }}
          maxHeight="500px"
          limit={25}
        />
      </Sheet>
    </>
  );
}
